import {
  DossierTable,
  Field,
  FieldProse,
  Gating,
  GatingSubsection,
  NumberedSection,
  Placeholder,
  ProseSubsection,
  CitationsUnderPlaceholderContext,
  CtdDossierContext,
  RegaraNote,
  SubsectionHeading,
  TitledSection,
  WhatBelongsNote,
  obj,
  regaraNoteText,
} from './DossierPrimitives';
import {
  EctdBibliography,
  EctdDisclaimer,
} from './EctdFrontMatter';
import { EctdVersionNote } from './EctdVersionNote';
import { EctdVersionNotesContext } from './EctdVersionNotesContext';
import { SectionGuidesContext, useSectionGuide } from './SectionGuidesContext';
import {
  debarmentQuestion,
  ectdVersionNotes,
  establishmentTable,
  isBiologicSubmission,
  isFilledSection,
  labelingSection,
  module1Sections,
  module1Subsections,
  sectionGuides,
  type EstablishmentTable as EstablishmentTableData,
  type Module1Section as Module1SectionData,
} from '@/features/dossier/ectdHeadings';
import type { EctdFrontMatter } from '@/features/dossier/api';
import { controlledClinicalTodo, module1Todo, sectionTodo } from '@/features/dossier/sectionTodos';

/**
 * eCTD (NDA/BLA) renderer — 5 modules per ICH common-technical-document
 * structure. Mirrors `renderEctdFromNewSchema` in `dossier-renderer.js`.
 *
 * Schema detection (in parent): presence of `module_1_administrative` in
 * the dossier dict. Modules 2–5 are ICH-harmonised and exported for the EU CTD
 * view (`EuCtdRenderer`), which brings its own EU Module 1 — as the PDF does.
 *
 * For a U.S. medicinal dossier (`submissionType`: NDA, 505(b)(2), ANDA, BLA, 351(k)), the
 * headings where eCTD v3.2.2 and v4.0 differ carry a one-line note naming both
 * (`EctdVersionNote`, each heading's `applies_to`); lockstep with `_with_version_notes` in the
 * PDF (`backend/services/dossier/ectd_renderer/usa.py`). Any other submission type, or none,
 * renders exactly as before. Each Modules 2 to 5 section also shows its REGARA guide under the
 * heading (`SectionGuidesContext`, read by `SubsectionHeading`; Task 3), lockstep with
 * `_section_guides_html` in the PDF; the EU CTD view provides none.
 *
 * A U.S. medicinal dossier's Module 1 also shows the further sections its pathway has (1.1
 * Forms, 1.3.2 Field copy certification for the drugs, 1.4 References, 1.4.4 Cross-reference,
 * 1.6 Meetings, 1.9, 1.12.11, 1.12.12 (a table) and 1.12.15 for the ANDA, 1.12.14, 1.14.1 for
 * the drugs and 1.14.3 for the ANDA under 1.14 Labeling, 1.16, 1.18, each section's
 * `applies_to`; `module1Sections`), each with what belongs there; lockstep with
 * `_module_1_section` in the PDF.
 */
export function EctdRenderer({
  dossier,
  submissionType = null,
  frontMatter = null,
}: {
  dossier: Record<string, unknown>;
  /** The run's submission_type ("bla", "nda", …); null when unknown. */
  submissionType?: string | null;
  /** The bibliography and disclaimer the PDF prints (`ectd_front_matter`
   *  on the run response); null shows none of them. */
  frontMatter?: EctdFrontMatter | null;
}) {
  // As in the eCTD PDF: letters keep their line breaks, AI tables are tables, no device wording.
  const biologic = isBiologicSubmission(submissionType);
  return (
    <CtdDossierContext.Provider value>
      <EctdVersionNotesContext.Provider value={ectdVersionNotes(submissionType)}>
        {/* U.S. only (EuCtdRenderer never sets it): a submitter box keeps its citations. */}
        <CitationsUnderPlaceholderContext.Provider value>
          <div className="space-y-4">
            <RegaraNotesSection dossier={dossier} />
            <Module1 m1={obj(dossier, 'module_1_administrative')} submissionType={submissionType} />
            {/* U.S. only (EuCtdRenderer never sets it): each section's REGARA guide (Task 3). */}
            <SectionGuidesContext.Provider value={sectionGuides(submissionType)}>
              <Module2 m2={obj(dossier, 'module_2_summaries')} />
              <Module3 m3={obj(dossier, 'module_3_quality')} />
              <Module4 m4={obj(dossier, 'module_4_nonclinical')} applicantStatement={biologic} />
              <Module5 m5={obj(dossier, 'module_5_clinical')} applicantStatement={biologic} />
            </SectionGuidesContext.Provider>
            {frontMatter && <EctdBibliography frontMatter={frontMatter} />}
            {frontMatter && <EctdDisclaimer frontMatter={frontMatter} />}
          </div>
        </CitationsUnderPlaceholderContext.Provider>
      </EctdVersionNotesContext.Provider>
    </CtdDossierContext.Provider>
  );
}

// ── Module 1 — Administrative Information (US-regional) ─────────────────

/**
 * The establishment list of Form FDA 356h, right after the 1.1 box (Task 18, RA finding 4): a
 * title line and the rows the run wrote from the documents, a to-do in a cell shown as the
 * submitter's box. Nothing when the pathway has no such table or the run wrote no row. Lockstep
 * with `_establishments` in `backend/services/dossier/ectd_renderer/usa.py`.
 */
function Establishments({
  table,
  m1,
}: {
  table: EstablishmentTableData | null;
  m1: Record<string, unknown>;
}) {
  const rows = table ? m1[table.key] : null;
  if (!table || !Array.isArray(rows)) return null;
  const objects = rows.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object');
  if (objects.length === 0) return null;
  return (
    <div className="my-3">
      <p className="font-semibold text-[13px]">{table.title}</p>
      <DossierTable
        headers={table.columns.map((c) => c.heading)}
        rows={objects.map((row) => table.columns.map((c) => row[c.key]))}
      />
    </div>
  );
}

/**
 * One of the further Module 1 sections (Task M1; drugs Task DA3): heading, what belongs there in
 * the REGARA box (`WhatBelongsNote`, Task UI1), then what the run wrote, outside the box; a
 * section that is the submitter's box shows its placeholder inside that same REGARA box. Nothing when the pathway does not have it
 * (`section` undefined). A `submitterOnly` section (1.1 Forms, 1.3.2 Field copy certification,
 * 1.14.1 / 1.14.3 labeling) is always the box (REGARA never fills in an FDA form, a
 * certification the applicant signs or labeling);
 * 1.4 / 1.4.4 / 1.6 / 1.12.15 / 1.16 show only when the run filled them. Lockstep with `_module_1_section`
 * in `backend/services/dossier/ectd_renderer/usa.py`.
 */
function Module1Section({
  section,
  m1,
}: {
  section: Module1SectionData | undefined;
  m1: Record<string, unknown>;
}) {
  if (!section) return null;
  const prose = section.submitterOnly ? null : m1[section.key];
  if (section.table) {
    // A table section (1.12.12): its rows, or the box. Lockstep with `_comparison_table`.
    // Only object rows are rows, as the PDF reads them (`_rows_table`).
    const rows = Array.isArray(prose)
      ? prose.filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      : [];
    if (section.shown === 'when_filled' && rows.length === 0) return null;
    const columns = section.table.columns;
    return (
      <div className="my-3">
        <SubsectionHeading number={section.number} title={section.title} />
        <WhatBelongsNote belongs={section.belongs}>
          {rows.length === 0 ? <Placeholder hint={section.placeholder} /> : null}
        </WhatBelongsNote>
        {rows.length > 0 && (
          <DossierTable
            headers={columns.map((c) => c.heading)}
            rows={rows.map((row) => columns.map((c) => row[c.key]))}
          />
        )}
      </div>
    );
  }
  if (section.shown === 'when_filled' && !isFilledSection(prose)) return null;
  // A blank body is the box, never empty paragraphs. Lockstep with `_module_1_section`.
  const written = prose as { body?: unknown; citations?: unknown[] } | null;
  const trimmed =
    written && typeof written === 'object' && typeof written.body === 'string'
      ? { ...written, body: written.body.trim() }
      : written;
  return (
    <ProseSubsection
      number={section.number}
      title={section.title}
      prose={trimmed}
      placeholderHint={section.placeholder}
      whatBelongs={section.belongs}
    />
  );
}

/**
 * 1.14 Labeling. A drug's is split per CTOC into its further sections (`module1Subsections`:
 * 1.14.1 Draft labeling, and the ANDA's 1.14.3 Listed drug labeling, both the submitter's box;
 * Task DB3), shown under the heading alone. A biologic keeps the single labeling section, shown
 * like a further section with its REGARA box (`labelingSection`: 21 CFR Part 201 and Part 610
 * Subpart G, Task QF); any other type keeps it as before. Lockstep with `_labeling` in
 * `backend/services/dossier/ectd_renderer/usa.py`.
 */
function Labeling({
  sections,
  m1,
  submissionType,
}: {
  sections: ReadonlyMap<string, Module1SectionData>;
  m1: Record<string, unknown>;
  submissionType: string | null;
}) {
  const children = module1Subsections(sections, '1.14');
  const single = labelingSection(submissionType);
  if (children.length === 0 && single !== null) {
    return <Module1Section section={single} m1={m1} />;
  }
  if (children.length === 0) {
    return (
      <ProseSubsection
        number="1.14"
        title="Labeling"
        prose={m1.labeling as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={module1Todo('labeling')}
      />
    );
  }
  return (
    <>
      <SubsectionHeading number="1.14" title="Labeling" />
      {children.map((section) => (
        <Module1Section key={section.number} section={section} m1={m1} />
      ))}
    </>
  );
}

/**
 * The REGARA check notes, in this order: the dossier key and the caption. They print in their own
 * "REGARA Notes" section before Module 1 (`RegaraNotesSection`), as the Word file prints them.
 * The order is lockstep with `_US_REGARA_NOTES` in the PDF (`backend/services/dossier/ectd_renderer/usa.py`).
 */
const REGARA_NOTES = [
  // Step 15, us_medicinal.checks.pathway_mismatch (the BLA, U.S. FDA follow-ups Task T4): first,
  // as the most consequential note.
  { key: 'pathway_note', caption: 'Pathway check (review before submission)' },
  // Step 4, us_medicinal.checks.guidance_currency (every U.S. medicinal run).
  { key: 'guidance_note', caption: 'Guidance check (review before submission)' },
  // Step 5, us_medicinal.checks.unverified_labels (every U.S. medicinal run).
  { key: 'label_note', caption: 'Label check (review before submission)' },
  // Step 6, us_medicinal.checks.guidance_scope (every U.S. medicinal run).
  { key: 'citation_scope_note', caption: 'Citation check (review before submission)' },
  // Step 8, us_medicinal.checks.application_numbers (the drugs, Task DB2).
  { key: 'application_number_note', caption: 'Application number check (review before submission)' },
  // Step 10, us_medicinal.checks.submission_history (every U.S. medicinal run, final review FR2).
  { key: 'submission_history_note', caption: 'Submission history check (review before submission)' },
  // Step 11, us_medicinal.checks.guidance_conformance (every U.S. medicinal run, final review FR4).
  { key: 'guidance_conformance_note', caption: 'Guidance conformance check (review before submission)' },
  // Steps 12-14, us_medicinal.checks.untitled_guidance / fda_agreement / legal_basis (the
  // biologics, U.S. FDA follow-ups Task T3).
  { key: 'untitled_guidance_note', caption: 'Untitled guidance check (review before submission)' },
  { key: 'fda_agreement_note', caption: 'FDA agreement check (review before submission)' },
  { key: 'legal_basis_note', caption: 'Legal basis check (review before submission)' },
  // Step 16, us_medicinal.checks.pediatric_dosing (the biologics, Task T4).
  { key: 'pediatric_dosing_note', caption: 'Pediatric dosing check (review before submission)' },
  // Step 17, us_medicinal.checks.q5e_comparability (the biologics, U.S. FDA follow-ups task LF).
  { key: 'q5e_comparability_note', caption: 'ICH Q5E comparability check (review before submission)' },
  // Step 18, us_medicinal.checks.combination_product (the biologics, plan boxes-and-writing Task 13).
  { key: 'combination_product_note', caption: 'Combination product check (review before submission)' },
  // Step 22, us_medicinal.checks.studies_certification (the biologics, plan boxes-and-writing Task 18).
  { key: 'studies_certification_note', caption: 'Form FDA 3674 check (review before submission)' },
  // Step 23, us_medicinal.checks.establishment_values (the biologics, plan boxes-and-writing Task 24).
  { key: 'establishment_check_note', caption: 'Establishment table check (review before submission)' },
  // Step 25, us_medicinal.checks.application_number_field (the biologics, Task 28): the documents
  // cite more than one BLA number, so the cover asks.
  { key: 'cover_application_number_note', caption: 'Application number check (review before submission)' },
  // Step 21, us_medicinal.checks.failed_lookups (the biologics, plan boxes-and-writing Task 19).
  { key: 'registry_lookup_note', caption: 'Register lookup check (review before submission)' },
] as const;

/** Lockstep with `REGARA_NOTES_TITLE` in `backend/services/vault_docx/us_medicinal.py`. */
const REGARA_NOTES_TITLE = 'REGARA Notes';

/**
 * Every REGARA check note in one section before Module 1 (founder 2026-11-02), each a REGARA box the
 * reader folds (open by default), as in the Word file, so the FDA text starts clean. Nothing when no
 * check has anything to say.
 */
function RegaraNotesSection({ dossier }: { dossier: Record<string, unknown> }) {
  const notes = REGARA_NOTES.filter(({ key }) => regaraNoteText(dossier[key]) !== null);
  if (notes.length === 0) return null;
  return (
    <TitledSection title={REGARA_NOTES_TITLE}>
      {notes.map(({ key, caption }) => (
        <RegaraNote key={key} body={dossier[key]} caption={caption} />
      ))}
    </TitledSection>
  );
}

function Module1({
  m1,
  submissionType,
}: {
  m1: Record<string, unknown>;
  submissionType: string | null;
}) {
  const applicant = obj(m1, 'applicant_information');
  const biologic = isBiologicSubmission(submissionType);
  // The sections this pathway has; none for a non-medicinal submission type.
  const sections = module1Sections(submissionType);
  return (
    <NumberedSection prefix="Module" num={1} title="Administrative Information">
      <Module1Section section={sections.get('1.1')} m1={m1} />
      <Establishments table={establishmentTable(submissionType)} m1={m1} />
      <ProseSubsection
        number="1.2"
        title="Cover Letter"
        prose={letterWithoutCitations(m1.cover_letter)}
        placeholderHint={module1Todo('cover_letter')}
      />

      <SubsectionHeading number="1.3.1" title="Applicant Information" />
      <Field
        label="Firm name"
        value={applicant.firm_name}
        fallbackHint={module1Todo('firm_name')}
      />
      <Field
        label="Establishment registration number"
        value={applicant.establishment_registration_number}
        fallbackHint={module1Todo('establishment_registration_number')}
      />
      <Field
        label="Street address"
        value={applicant.street_address}
        fallbackHint={module1Todo('street_address')}
      />
      <Field
        label="U.S. agent"
        value={applicant.us_agent}
        fallbackHint={module1Todo('us_agent')}
      />
      <Field
        label="Official correspondent"
        value={applicant.official_correspondent}
        fallbackHint={module1Todo('official_correspondent')}
      />
      <Module1Section section={sections.get('1.3.2')} m1={m1} />

      <GatingSubsection
        number="1.3.3"
        title="Debarment Certification"
        gating={
          m1.debarment_certification as
            | { gating_response?: unknown; rationale?: unknown }
            | undefined
        }
        question={debarmentQuestion(submissionType)}
        applicantStatement={biologic}
      />
      <GatingSubsection
        number="1.3.4"
        title="Financial Certification or Disclosure"
        gating={
          m1.financial_certification as
            | { gating_response?: unknown; rationale?: unknown }
            | undefined
        }
        question="Are clinical investigator financial certifications/disclosures required (21 CFR Part 54)?"
        applicantStatement={biologic}
      />
      <ProseSubsection
        number="1.3.5"
        title="Patent and Exclusivity"
        prose={m1.patent_exclusivity as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={module1Todo('patent_exclusivity')}
      />
      <Module1Section section={sections.get('1.4')} m1={m1} />
      <Module1Section section={sections.get('1.4.4')} m1={m1} />
      <Module1Section section={sections.get('1.6')} m1={m1} />
      <Module1Section section={sections.get('1.9')} m1={m1} />
      <Module1Section section={sections.get('1.12.11')} m1={m1} />
      <Module1Section section={sections.get('1.12.12')} m1={m1} />
      <Module1Section section={sections.get('1.12.14')} m1={m1} />
      <Module1Section section={sections.get('1.12.15')} m1={m1} />
      <Labeling sections={sections} m1={m1} submissionType={submissionType} />
      <GatingSubsection
        number="1.15"
        title="Promotional Material"
        gating={
          m1.promotional_material as
            | { gating_response?: unknown; rationale?: unknown }
            | undefined
        }
        question="Is promotional material being submitted with this application?"
        applicantStatement={biologic}
      />
      <Module1Section section={sections.get('1.16')} m1={m1} />
      <Module1Section section={sections.get('1.18')} m1={m1} />
    </NumberedSection>
  );
}

// ── Module 2 — Summaries (ICH-harmonized) ───────────────────────────────

/**
 * A cover letter as rendered: its body only. A letter ends at the signature — the
 * agent's citation list printed under it read as working notes (audit 2026-10-12).
 * Lockstep with `letter_without_citations` in the eCTD PDF.
 */
export function letterWithoutCitations(prose: unknown): { body?: unknown } | null {
  return prose && typeof prose === 'object' ? { body: (prose as { body?: unknown }).body } : null;
}

export function Module2({ m2 }: { m2: Record<string, unknown> }) {
  return (
    <NumberedSection prefix="Module" num={2} title="Summaries">
      <ProseSubsection
        number="2.2"
        title="Introduction to Summary"
        prose={m2.introduction as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('2.2')}
      />
      <ProseSubsection
        number="2.3"
        title="Quality Overall Summary"
        prose={m2.quality_overall_summary as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('2.3')}
      />
      <ProseSubsection
        number="2.4"
        title="Nonclinical Overview"
        prose={m2.nonclinical_overview as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('2.4')}
      />
      <ProseSubsection
        number="2.5"
        title="Clinical Overview"
        prose={m2.clinical_overview as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('2.5')}
      />
      <ProseSubsection
        number="2.6"
        title="Nonclinical Written and Tabulated Summaries"
        prose={
          m2.nonclinical_written_tabulated_summaries as
            | { body?: unknown; citations?: unknown[] }
            | null
        }
        placeholderHint={sectionTodo('2.6')}
      />
      <ProseSubsection
        number="2.7"
        title="Clinical Summary"
        prose={m2.clinical_summary as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('2.7')}
      />
    </NumberedSection>
  );
}

// ── Module 3 — Quality (CMC): Drug Substance + Drug Product ─────────────

export function Module3({
  m3,
  regionalHint = sectionTodo('3.2.R'),
}: {
  m3: Record<string, unknown>;
  /** Placeholder hint for 3.2.R, the one region-specific Module 3 section. */
  regionalHint?: string;
}) {
  const ds = obj(m3, 'drug_substance');
  const dp = obj(m3, 'drug_product');
  return (
    <NumberedSection prefix="Module" num={3} title="Quality (CMC)">
      <h4 className="text-[15px] font-bold text-navy mt-2 mb-1">
        3.2.S Drug Substance
      </h4>
      <EctdVersionNote number="3.2.S" />
      <PartSummary node={ds} />
      <ProseSubsection
        number="3.2.S.1"
        title="General Information"
        prose={ds.general_information as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.S.1')}
      />
      <ProseSubsection
        number="3.2.S.2"
        title="Manufacture"
        prose={ds.manufacture as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.S.2')}
      />
      <ProseSubsection
        number="3.2.S.3"
        title="Characterization"
        prose={ds.characterization as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.S.3')}
      />
      <ProseSubsection
        number="3.2.S.4"
        title="Control of Drug Substance"
        prose={ds.control_of_drug_substance as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.S.4')}
      />
      <ProseSubsection
        number="3.2.S.5"
        title="Reference Standards or Materials"
        prose={ds.reference_standards as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.S.5')}
      />
      <ProseSubsection
        number="3.2.S.6"
        title="Container Closure System"
        prose={ds.container_closure_system as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.S.6')}
      />
      <ProseSubsection
        number="3.2.S.7"
        title="Stability"
        prose={ds.stability as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.S.7')}
      />

      <h4 className="text-[15px] font-bold text-navy mt-5 mb-1">
        3.2.P Drug Product
      </h4>
      <EctdVersionNote number="3.2.P" />
      <PartSummary node={dp} />
      <ProseSubsection
        number="3.2.P.1"
        title="Description and Composition"
        prose={dp.description_and_composition as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.P.1')}
      />
      <ProseSubsection
        number="3.2.P.2"
        title="Pharmaceutical Development"
        prose={dp.pharmaceutical_development as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.P.2')}
      />
      <ProseSubsection
        number="3.2.P.3"
        title="Manufacture"
        prose={dp.manufacture as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.P.3')}
      />
      <ProseSubsection
        number="3.2.P.4"
        title="Control of Excipients"
        prose={dp.control_of_excipients as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.P.4')}
      />
      <ProseSubsection
        number="3.2.P.5"
        title="Control of Drug Product"
        prose={dp.control_of_drug_product as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.P.5')}
      />
      <ProseSubsection
        number="3.2.P.6"
        title="Reference Standards or Materials"
        prose={dp.reference_standards as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.P.6')}
      />
      <ProseSubsection
        number="3.2.P.7"
        title="Container Closure System"
        prose={dp.container_closure_system as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.P.7')}
      />
      <ProseSubsection
        number="3.2.P.8"
        title="Stability"
        prose={dp.stability as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.P.8')}
      />

      <ProseSubsection
        number="3.2.A"
        title="Appendices"
        prose={m3.appendices as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.2.A')}
      />
      <ProseSubsection
        number="3.2.R"
        title="Regional Information"
        prose={m3.regional_information as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={regionalHint}
      />
      <ProseSubsection
        number="3.3"
        title="Literature References"
        prose={m3.literature_references as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('3.3')}
      />
    </NumberedSection>
  );
}

// ── Module 4 — Nonclinical Study Reports (all gated) ────────────────────

export function Module4({
  m4,
  authority,
  applicantStatement,
}: {
  m4: Record<string, unknown>;
  /** The regulator named in the yes/no advisory boxes (default FDA). */
  authority?: string;
  /** U.S. biologic: a "No" reason prints as the applicant's statement (see `Gating`). */
  applicantStatement?: boolean;
}) {
  return (
    <NumberedSection prefix="Module" num={4} title="Nonclinical Study Reports">
      <EctdVersionNote number="4" />
      <GatingSubsection
        number="4.2.1"
        title="Pharmacology"
        gating={
          m4.pharmacology as { gating_response?: unknown; rationale?: unknown } | undefined
        }
        question="Are pharmacology study reports provided in this submission?"
        authority={authority}
        applicantStatement={applicantStatement}
      />
      <GatingSubsection
        number="4.2.2"
        title="Pharmacokinetics"
        gating={
          m4.pharmacokinetics as
            | { gating_response?: unknown; rationale?: unknown }
            | undefined
        }
        question="Are pharmacokinetic study reports provided in this submission?"
        authority={authority}
        applicantStatement={applicantStatement}
      />
      <GatingSubsection
        number="4.2.3"
        title="Toxicology"
        gating={
          m4.toxicology as { gating_response?: unknown; rationale?: unknown } | undefined
        }
        question="Are toxicology study reports provided in this submission?"
        authority={authority}
        applicantStatement={applicantStatement}
      />
      <ProseSubsection
        number="4.3"
        title="Literature References"
        prose={m4.literature_references as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('4.3')}
      />
    </NumberedSection>
  );
}

// ── Module 5 — Clinical Study Reports ───────────────────────────────────

export function Module5({
  m5,
  authority,
  applicantStatement,
}: {
  m5: Record<string, unknown>;
  /** The regulator named in the yes/no advisory boxes (default FDA). */
  authority?: string;
  /** U.S. biologic: a "No" reason prints as the applicant's statement (see `Gating`). */
  applicantStatement?: boolean;
}) {
  return (
    <NumberedSection prefix="Module" num={5} title="Clinical Study Reports">
      <EctdVersionNote number="5" />
      <ProseSubsection
        number="5.2"
        title="Tabular Listing of All Clinical Studies"
        prose={m5.tabular_listing_of_studies as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('5.2')}
      />
      <GatingSubsection
        number="5.3.1"
        title="Reports of Biopharmaceutic Studies"
        gating={
          m5.biopharmaceutic_studies as
            | { gating_response?: unknown; rationale?: unknown }
            | undefined
        }
        question="Are biopharmaceutic (BA/BE) study reports provided?"
        authority={authority}
        applicantStatement={applicantStatement}
      />
      <GatingSubsection
        number="5.3.3"
        title="Reports of Human Pharmacokinetic Studies"
        gating={
          m5.pharmacokinetic_studies as
            | { gating_response?: unknown; rationale?: unknown }
            | undefined
        }
        question="Are human pharmacokinetic study reports provided?"
        authority={authority}
        applicantStatement={applicantStatement}
      />
      <ControlledClinical
        cs={obj(m5, 'controlled_clinical_studies')}
        authority={authority}
        applicantStatement={applicantStatement}
      />
      <GatingSubsection
        number="5.3.5.4"
        title="Other Study Reports"
        gating={
          m5.other_study_reports as
            | { gating_response?: unknown; rationale?: unknown }
            | undefined
        }
        question="Are other clinical study reports (PMS, registries, exploratory) provided?"
        authority={authority}
        applicantStatement={applicantStatement}
      />
      <ProseSubsection
        number="5.4"
        title="Literature References"
        prose={m5.literature_references as { body?: unknown; citations?: unknown[] } | null}
        placeholderHint={sectionTodo('5.4')}
      />
    </NumberedSection>
  );
}

/**
 * 5.3.5 Reports of Efficacy and Safety Studies — gating block with five
 * nested prose sub-fields when gated "Yes".
 */
function ControlledClinical({
  cs,
  authority,
  applicantStatement,
}: {
  cs: Record<string, unknown>;
  authority?: string;
  applicantStatement?: boolean;
}) {
  const isYes = String(cs.gating_response ?? '').trim().toLowerCase() === 'yes';
  const guide = useSectionGuide('5.3.5');
  const subFields: { label: string; key: string }[] = [
    { label: 'Study summary', key: 'study_summary' },
    { label: 'Efficacy results', key: 'efficacy_results' },
    { label: 'Safety analysis', key: 'safety_analysis' },
    { label: 'Risk-benefit analysis', key: 'risk_benefit_analysis' },
    { label: 'Statistical analysis', key: 'statistical_analysis' },
  ];
  return (
    <div className="my-3">
      <SubsectionHeading
        number="5.3.5"
        title="Reports of Efficacy and Safety Studies (Controlled Clinical Studies)"
        showGuide={false}
      />
      {/* The shared yes/no block, with its rationale — the PDF prints it; the inline
          question/response block this replaces dropped it. It places the section's guide. */}
      <Gating
        question="Are controlled clinical studies relied upon for this submission?"
        section={cs}
        authority={authority}
        applicantStatement={applicantStatement}
        guide={guide}
      />
      <PartSummary node={cs} />
      {isYes &&
        subFields.map((sf) => {
          const sub = cs[sf.key];
          const body = sub && typeof sub === 'object' ? (sub as { body?: unknown }).body : null;
          return (
            <FieldProse key={sf.key} label={sf.label} body={body} fallbackHint={controlledClinicalTodo(sf.key)} />
          );
        })}
    </div>
  );
}

/**
 * Text the agent wrote for a whole part (3.2.S, 3.2.P, 5.3.5) beside its
 * subsections — a part-level `body`. Shown under the part's heading so drafted
 * content is never silently dropped (run 518, memo M-RA-261009-BA 001); nothing
 * when there is none. Lockstep with `_part_summary` in the eCTD PDF.
 */
function PartSummary({ node }: { node: Record<string, unknown> }) {
  const body = typeof node.body === 'string' ? node.body.trim() : '';
  if (!body || body.startsWith('__PLACEHOLDER__')) return null;
  const citations = Array.isArray(node.citations) ? node.citations.filter(Boolean) : [];
  return (
    <div className="my-2">
      <FieldProse body={body} />
      {citations.length > 0 && (
        <div className="text-[13.5px]">
          <p className="m-0 mb-1">
            <em>Citations:</em>
          </p>
          <ul className="list-disc pl-5 m-0 space-y-0.5 text-[12.5px]">
            {citations.map((c, i) => (
              <li key={i}>{String(c)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
